import * as React from "react";

import { MontadorOperacao } from "@/components/canvas/MontadorOperacao";
import { ComSlotAcoes } from "@/components/layout/SlotAcoes";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useModelos } from "@/hooks/queries";
import type { TipoNo } from "@/types";

const PAPEIS_DIST: TipoNo[] = ["PRODUTOR", "DISTFS", "ARMAZEM", "DISTRIBUIDORA"];

/** Catálogo estático transcrito do deck da Distribuidora — referência de consulta. */
function FluxosDoDeck() {
  const { data, isLoading } = useModelos();
  const [chave, setChave] = React.useState<string>("venda_direta");

  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;

  const fluxo = data.distFluxos[chave];
  if (!fluxo) return null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>{fluxo.nome}</CardTitle>
              <CardDescription>{fluxo.produto}</CardDescription>
            </div>
            <Select value={chave} onValueChange={setChave}>
              <SelectTrigger className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {data.distOrdem.map((id) => (
                  <SelectItem key={id} value={id}>
                    {data.distFluxos[id]?.nome ?? id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {fluxo.cadeia.map((elo, i) => (
              <React.Fragment key={`${elo.label}-${i}`}>
                <div
                  className="rounded-lg border-2 px-3 py-1.5 text-xs font-bold"
                  style={{ borderColor: elo.cor, color: elo.cor }}
                >
                  {elo.label}
                  <span className="ml-1 text-[10px] text-muted-foreground">{elo.uf}</span>
                </div>
                {i < fluxo.cadeia.length - 1 && <span className="text-muted-foreground">→</span>}
              </React.Fragment>
            ))}
          </div>
          {fluxo.nota && <p className="text-sm text-muted-foreground">{fluxo.nota}</p>}
          {fluxo.atencao && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              <b>Atenção: </b>
              {fluxo.atencao}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notas do fluxo</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="min-w-[52rem]">
            <TableHeader>
              <TableRow>
                <TableHead>NF</TableHead>
                <TableHead className="text-left">Operação</TableHead>
                <TableHead>CFOP</TableHead>
                <TableHead>ICMS</TableHead>
                <TableHead>CST</TableHead>
                <TableHead>PIS/COFINS</TableHead>
                <TableHead>CST</TableHead>
                <TableHead>IPI</TableHead>
                <TableHead className="text-left">Informações complementares</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fluxo.nfs.map((nf) => (
                <TableRow key={nf.n}>
                  <TableCell className="p-0">
                    <div className="flex h-full min-h-10 w-8 items-center justify-center bg-fs-verde text-sm font-extrabold text-white">
                      {nf.n}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-56">
                    <div className="font-extrabold text-fs-escuro">
                      {nf.rotulo} · {nf.fluxo}
                    </div>
                    <div className="text-muted-foreground">{nf.operacao}</div>
                  </TableCell>
                  <TableCell className="text-center font-extrabold numerico">{nf.cfop}</TableCell>
                  <TableCell className="text-center font-extrabold numerico">{nf.icms}</TableCell>
                  <TableCell className="text-center numerico">{nf.cstIcms}</TableCell>
                  <TableCell className="whitespace-nowrap text-center numerico">{nf.pis}</TableCell>
                  <TableCell className="text-center numerico">{nf.cstPis}</TableCell>
                  <TableCell className="text-center numerico">
                    {nf.ipi}
                    {nf.cide && <Badge variant="neutro" className="ml-1">CIDE {nf.cide}</Badge>}
                  </TableCell>
                  <TableCell className="min-w-72 text-[11px] text-muted-foreground">
                    {nf.info}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function VendasDistribuidoraPage() {
  return (
    <div className="space-y-5">
      <Tabs defaultValue="montador">
        <ComSlotAcoes
          abas={
            <TabsList>
              <TabsTrigger value="montador">Montador</TabsTrigger>
              <TabsTrigger value="deck">Fluxos do deck</TabsTrigger>
            </TabsList>
          }
        >
          <TabsContent value="montador">
            <MontadorOperacao
              contexto="distribuidora"
              chaveModelo="distribuidora"
              titulo="Distribuidora"
              descricao="Cadeia venda → transferência → revenda, com venda à ordem triangular e regimes de EAC, EHC e diesel."
              papeis={PAPEIS_DIST}
              produtos={["anidro", "hidratado", "diesel_a", "diesel_b"]}
            />
          </TabsContent>

          <TabsContent value="deck">
            <FluxosDoDeck />
          </TabsContent>
        </ComSlotAcoes>
      </Tabs>
    </div>
  );
}
